import React, { useEffect, useRef, useState } from "react";
import { ISession } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import moment from "moment-timezone";

import Button from "src/components/Button/Button";
import DatePick from "src/components/DatePick/DatePick";
import Input from "src/components/Input/Input";
import Confirm from "src/components/Confirm/Confirm";
import { toast } from "src/components/Toast/ToastManager";
import Session from "./Session/Session";
import {
    generateWeeklySessions,
    isWeeklySeries,
    registrationBlockMessage,
    sessionEntryCount,
    weekdayLabel,
    weeklyScheduleZone,
    weeklySummary,
} from "./weeklySchedule";

import styles from "./CourseSchedule.module.scss";

interface IProps {
    sessions?: ISession[];
    timezone?: string;
    courseType?: string;
    entryLimit?: number;
    waitlistSize?: number;
    onChange?: (sessions: ISession[]) => void;
    onLimitsChange?: (entryLimit: number, waitlistSize: number) => void;
}

const defaultStart = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const defaultFrom = () => {
    const now = new Date();
    return new Date(2000, 0, 1, now.getHours(), 0, 0, 0);
};

const CourseSchedule: React.FC<IProps> = (props) => {
    const zone = weeklyScheduleZone(props.timezone);
    const incoming = props.sessions && props.sessions.length > 0 ? props.sessions : [];
    const orderedIncoming = [...incoming].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
    const opensWeekly = incoming.length === 0 || isWeeklySeries(incoming, zone);
    const first = (opensWeekly ? orderedIncoming : incoming)[0];
    const firstZoned = first ? moment.tz(first.start_date, zone) : null;
    const firstEnd = first ? moment.tz(first.end_date, zone) : null;
    const initialStart = firstZoned ? new Date(firstZoned.year(), firstZoned.month(), firstZoned.date()) : defaultStart();
    const initialFrom = firstZoned ? new Date(2000, 0, 1, firstZoned.hour(), firstZoned.minute(), 0, 0) : defaultFrom();
    const initialTo = firstEnd ? new Date(2000, 0, 1, firstEnd.hour(), firstEnd.minute(), 0, 0) : new Date(2000, 0, 1, defaultFrom().getHours() + 1, 0, 0, 0);
    const initialWeeks = incoming.length > 0 ? incoming.length : 6;
    const initialFirstName = first?.instructors?.[0]?.first_name || "";
    const initialLastName = first?.instructors?.[0]?.last_name || "";
    const initialCapacity = first?.regular_capacity ?? 5;
    const initialWaitlist = first?.waitlist_size ?? 0;
    const courseLevel = props.courseType === Constants.course_type.full_course || props.courseType === Constants.course_type.seminar;

    const savedWithEntries = useRef(
        incoming.filter((session) => session._id && sessionEntryCount(session) > 0)
    );
    const preservedIds = useRef<string[]>((opensWeekly ? orderedIncoming : incoming).map((session) => session._id || ""));
    const entriesById = useRef(new Map(incoming.filter((session) => session._id).map((session) => [String(session._id), session])));
    const toEdited = useRef(false);
    const skipNextGenerate = useRef(true);

    const [mode, setMode] = useState<"weekly" | "custom">(opensWeekly ? "weekly" : "custom");
    const [startDate, setStartDate] = useState<Date>(initialStart);
    const [weeks, setWeeks] = useState<number>(initialWeeks);
    const [weeksText, setWeeksText] = useState(String(initialWeeks));
    const [fromTime, setFromTime] = useState<Date>(initialFrom);
    const [toTime, setToTime] = useState<Date>(initialTo);
    const [firstName, setFirstName] = useState(initialFirstName);
    const [lastName, setLastName] = useState(initialLastName);
    const [capacity, setCapacity] = useState<number>(initialCapacity);
    const [classWaitlist, setClassWaitlist] = useState<number>(initialWaitlist);
    const [confirmWeekly, setConfirmWeekly] = useState(false);
    const [rerender, setRerender] = useState(false);

    const [sessions, setSessions] = useState<ISession[]>(() => {
        if (incoming.length > 0) {
            return opensWeekly ? orderedIncoming : incoming;
        }
        return generateWeeklySessions({
            startDate: initialStart,
            fromTime: initialFrom,
            toTime: initialTo,
            weeks: initialWeeks,
            firstName: initialFirstName,
            lastName: initialLastName,
            capacity: initialCapacity,
            waitlistSize: initialWaitlist,
            zone,
            preservedIds: [],
            entriesById: new Map(),
        });
    });

    const buildWeekly = (nextWeeks: number, nextStart: Date, nextFrom: Date, nextTo: Date, nextFirst: string, nextLast: string, nextCapacity: number, nextWaitlist: number) => {
        return generateWeeklySessions({
            startDate: nextStart,
            fromTime: nextFrom,
            toTime: nextTo,
            weeks: nextWeeks,
            firstName: nextFirst,
            lastName: nextLast,
            capacity: nextCapacity,
            waitlistSize: nextWaitlist,
            zone,
            preservedIds: preservedIds.current,
            entriesById: entriesById.current,
        });
    };

    useEffect(() => {
        if (skipNextGenerate.current) {
            skipNextGenerate.current = false;
            return;
        }
        if (mode !== "weekly") {
            return;
        }
        setSessions(buildWeekly(weeks, startDate, fromTime, toTime, firstName, lastName, capacity, classWaitlist));
    // buildWeekly closes over refs only
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, weeks, startDate, fromTime, toTime, firstName, lastName, capacity, classWaitlist]);

    useEffect(() => {
        if (rerender) {
            setRerender(false);
        }
    }, [rerender]);

    useEffect(() => {
        props.onChange && props.onChange(sessions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessions]);

    const droppedDates = () => {
        const kept = new Set(sessions.map((session) => session._id).filter(Boolean).map(String));
        return savedWithEntries.current
            .filter((session) => !kept.has(String(session._id)))
            .map((session) => new Date(session.start_date));
    };

    const blockMessage = () => {
        const dates = droppedDates();
        return dates.length ? registrationBlockMessage(dates, zone) : "";
    };

    const seedFromSessions = (source: ISession[]) => {
        const ordered = [...source].sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
        const lead = ordered[0];
        if (!lead) {
            return;
        }
        const zoned = moment.tz(lead.start_date, zone);
        const end = moment.tz(lead.end_date, zone);
        preservedIds.current = ordered.map((session) => session._id || "");
        toEdited.current = false;
        setStartDate(new Date(zoned.year(), zoned.month(), zoned.date()));
        const nextWeeks = Math.min(52, Math.max(1, ordered.length));
        setWeeks(nextWeeks);
        setWeeksText(String(nextWeeks));
        setFromTime(new Date(2000, 0, 1, zoned.hour(), zoned.minute(), 0, 0));
        setToTime(new Date(2000, 0, 1, end.hour(), end.minute(), 0, 0));
        setFirstName(lead.instructors?.[0]?.first_name || "");
        setLastName(lead.instructors?.[0]?.last_name || "");
        setCapacity(lead.regular_capacity ?? 5);
        setClassWaitlist(lead.waitlist_size ?? 0);
    };

    const requestWeekly = () => {
        if (mode === "weekly") {
            return;
        }
        if (isWeeklySeries(sessions, zone)) {
            seedFromSessions(sessions);
            setMode("weekly");
            return;
        }
        setConfirmWeekly(true);
    };

    const addSession = () => {
        setSessions([
            ...sessions,
            {
                _id: "",
                start_date: sessions.length > 0 && sessions[sessions.length - 1].start_date ? moment(sessions[sessions.length - 1].start_date).add(7, "days").toDate() : moment(new Date()).minute(0).second(0).toDate(),
                end_date: sessions.length > 0 && sessions[sessions.length - 1].end_date ? moment(sessions[sessions.length - 1].end_date).add(7, "days").toDate() : moment(new Date()).minute(0).second(0).add(1, "hours").toDate(),
                instructors: sessions.length > 0 && sessions[sessions.length - 1].instructors.length > 0 ? [{
                    _id: sessions[sessions.length - 1].instructors[0]._id,
                    first_name: sessions[sessions.length - 1].instructors[0].first_name,
                    last_name: sessions[sessions.length - 1].instructors[0].last_name,
                    contact_type: sessions[sessions.length - 1].instructors[0].contact_type || "Instructor",
                    phone: sessions[sessions.length - 1].instructors[0].phone || "123",
                    email: sessions[sessions.length - 1].instructors[0].email || "email"
                }] : [],
                regular_capacity: sessions.length > 0 && sessions[sessions.length - 1].regular_capacity ? sessions[sessions.length - 1].regular_capacity : 5,
                dropin_capacity: sessions.length > 0 && sessions[sessions.length - 1].regular_capacity ? sessions[sessions.length - 1].regular_capacity : 5,
                waitlist_size: sessions.length > 0 ? (sessions[sessions.length - 1].waitlist_size || 0) : 0,
            }
        ]);
    };

    const deleteSession = (index: number) => {
        const session = sessions[index];
        if (session && sessionEntryCount(session) > 0) {
            toast.show({
                title: "Schedule",
                content: registrationBlockMessage([new Date(session.start_date)], zone),
                duration: 8000,
                type: "fail",
            });
            return;
        }
        setSessions(sessions.filter((_, i) => i !== index));
        setRerender(true);
    };

    const onFromChange = (date: Date) => {
        let duration = toTime.getTime() - fromTime.getTime();
        if (duration <= 0) {
            duration += 24 * 60 * 60 * 1000;
        }
        setFromTime(date);
        if (!toEdited.current) {
            setToTime(new Date(date.getTime() + duration));
        }
    };

    const warning = mode === "weekly" ? blockMessage() : "";
    const limitFields = courseLevel ? (
        <div className={styles.row}>
            <div className={styles.col}>
                <Input
                    label="# of entries"
                    type="number"
                    value={String(props.entryLimit ?? 0)}
                    onChange={(e) => {
                        const next = parseInt(e.target.value, 10);
                        props.onLimitsChange?.(isNaN(next) ? 0 : next, props.waitlistSize || 0);
                    }}
                />
            </div>
            <div className={styles.col}>
                <Input
                    label="Waitlist size"
                    type="number"
                    value={props.waitlistSize === undefined || props.waitlistSize === null ? "" : String(props.waitlistSize)}
                    onChange={(e) => {
                        const raw = e.target.value;
                        const next = raw === "" ? 0 : parseInt(raw, 10);
                        props.onLimitsChange?.(props.entryLimit || 0, isNaN(next) ? 0 : next);
                    }}
                />
            </div>
        </div>
    ) : null;

    return (
        <div className={styles.CourseSchedule}>
            {limitFields}
            <div className={styles.viewToggle}>
                <button type="button" className={`${styles.toggleBtn} ${mode === "weekly" ? styles.toggleActive : ""}`} onClick={requestWeekly}>Repeats weekly</button>
                <button type="button" className={`${styles.toggleBtn} ${mode === "custom" ? styles.toggleActive : ""}`} onClick={() => setMode("custom")}>Custom dates</button>
            </div>
            {confirmWeekly && (
                <Confirm
                    message="Switching to Repeats weekly rebuilds the sessions into one weekly series. Dates that don't follow that series will be replaced."
                    onCancel={() => setConfirmWeekly(false)}
                    onConfirm={() => {
                        seedFromSessions(sessions);
                        setConfirmWeekly(false);
                        setMode("weekly");
                    }}
                />
            )}
            {mode === "weekly" && (
                <div className={styles.weekly}>
                    <div className={styles.row}>
                        <div className={styles.col}>
                            <DatePick label="Starts" value={startDate} onChange={(date: Date) => date && setStartDate(date)} />
                            <div className={styles.weekday}>{weekdayLabel(startDate)}</div>
                        </div>
                        <div className={styles.col} onBlur={() => {
                            const next = parseInt(weeksText, 10);
                            const clamped = isNaN(next) ? weeks : Math.min(52, Math.max(1, next));
                            setWeeks(clamped);
                            setWeeksText(String(clamped));
                        }}>
                            <Input
                                label="For"
                                type="text"
                                inputMode="numeric"
                                value={weeksText}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw !== "" && !/^\d+$/.test(raw)) {
                                        return;
                                    }
                                    setWeeksText(raw);
                                    const next = parseInt(raw, 10);
                                    if (!isNaN(next) && next >= 1 && next <= 52) {
                                        setWeeks(next);
                                    }
                                }}
                            />
                            <div className={styles.weekday}>weeks</div>
                        </div>
                    </div>
                    <div className={styles.row}>
                        <div className={styles.col}>
                            <DatePick timeOnly label="From" value={fromTime} onChange={(date: Date) => date && onFromChange(date)} />
                        </div>
                        <div className={styles.col}>
                            <DatePick timeOnly label="To" value={toTime} onChange={(date: Date) => {
                                if (!date) return;
                                toEdited.current = true;
                                setToTime(date);
                            }} />
                        </div>
                    </div>
                    <div className={styles.row}>
                        <div className={styles.col}>
                            <Input label="Instructor First Name" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                        </div>
                        <div className={styles.col}>
                            <Input label="Instructor Last Name" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                        </div>
                    </div>
                    {!courseLevel && <div className={styles.row}>
                        <div className={styles.col}>
                            <Input
                                label="Capacity"
                                type="number"
                                value={String(capacity)}
                                onChange={(e) => {
                                    const next = parseInt(e.target.value, 10);
                                    setCapacity(isNaN(next) ? 0 : next);
                                }}
                            />
                        </div>
                        <div className={styles.col}>
                            <Input
                                label="Waitlist size"
                                type="number"
                                value={String(classWaitlist)}
                                onChange={(e) => {
                                    const next = parseInt(e.target.value, 10);
                                    setClassWaitlist(isNaN(next) ? 0 : next);
                                }}
                            />
                        </div>
                    </div>}
                    <div className={styles.summary}>{weeklySummary(sessions, zone)}</div>
                    <ul className={styles.preview}>
                        {sessions.map((session, index) => (
                            <li key={session._id || index}>
                                {moment.tz(session.start_date, zone).format("ddd MMM D")} · {moment.tz(session.start_date, zone).format("h:mm A")}–{moment.tz(session.end_date, zone).format("h:mm A")}
                            </li>
                        ))}
                    </ul>
                    {warning && <p className={styles.warning}>{warning}</p>}
                    <button type="button" className={styles.customize} onClick={() => setMode("custom")}>Customize individual sessions</button>
                </div>
            )}
            {mode === "custom" && (
                <>
                    {!rerender && sessions.map((session, index) => (
                        <Session
                            key={session._id || index}
                            index={index + 1}
                            sessionId={session._id || ""}
                            start={session.start_date}
                            end={session.end_date}
                            regularCapacity={session.regular_capacity || 5}
                            dropinCapacity={session.dropin_capacity || 0}
                            waitlistSize={session.waitlist_size || 0}
                            hideCapacity={courseLevel}
                            instructors={session.instructors}
                            onDelete={() => deleteSession(index)}
                            className={index === sessions.length - 1 ? styles.last : ""}
                            hideDelete={sessions.length > 1 ? false : true}
                            onChange={(updatedSession: any) => {
                                const updatedSessions = [...sessions];
                                updatedSessions[index] = { ...updatedSessions[index], ...updatedSession };
                                setSessions(updatedSessions);
                            }}
                        />
                    ))}
                    <div className={styles.actions}>
                        <Button className={styles.addAnotherSession} type="button" onClick={() => { addSession(); }}><span className={`${styles.icon} ${styles.plus}`}></span><span>{sessions.length === 0 ? "Add a Session" : "Add Another Session"}</span></Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default CourseSchedule;
